/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Autocomplete from '@mui/material/Autocomplete';
import AutocompleteField from 'components/AutocompleteField';
import Checkbox from '@mui/material/Checkbox';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { recordProperties } from 'containers/Base/styles';
import { textFieldDataValid } from 'containers/Base/utilities';
import type { HTMLAttributes } from 'react';
import type { TParsedField, TSchema } from 'types/items';
import type { WorkingState } from '../../Data/data.types';

const formatEpcFn = (newValue: string) => {
  const epcUpper = newValue.toUpperCase().replace(/-/g, '');
  return epcUpper.match(/.{1,4}/g)?.join('-') || '';
};

interface Props extends HTMLAttributes<HTMLDivElement> {
  fields: any[];
  mode: 'add' | 'edit';
  record: any;
  setRecord: (updatedRecord: any[]) => void;
  schema: TSchema<any>;
  workingState: WorkingState;
}

export default function RecordProperties({
  fields,
  mode,
  record,
  schema,
  setRecord,
  workingState,
}: Props) {
  const { classes } = recordProperties();

  // Handlers
  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const field: TParsedField = fields.find(
      (f) => f.fieldName === e.target.name
    );
    if (!textFieldDataValid(e.target.value, field)) {
      return;
    }

    let { value } = e.target;
    if (field?.uiProperties?.isEPC) {
      value = formatEpcFn(value);
    }

    setRecord({ ...record, [e.target.name]: value });
  };

  return (
    <div>
      {fields
        .filter((f) => f.required !== 'A' && f.uiType !== 'none')
        .map((field, idx) => (
          <div key={field.fieldName}>
            {(field.type === 'string' ||
              field.type === 'int' ||
              field.type === 'float') && (
              <Tooltip
                title={field.description}
                disableHoverListener
                placement="top-end"
              >
                <div>
                  {field.uiType === 'autocomplete' && (
                    <AutocompleteField
                      className={classes.autoComplete}
                      configuration={{
                        itemType: field.uiProperties.itemType,
                      }}
                      disabled={
                        workingState?.adding ||
                        workingState?.updating ||
                        (field.fieldName === schema?.hierarchy?.primaryKey &&
                          mode === 'edit') ||
                        field.editable !== 'true'
                      }
                      idColumn={field.uiProperties.idColumn}
                      labelColumns={[field.uiProperties.labelColumn]}
                      label={field.label}
                      onChange={(event: any, newValue: any) => {
                        setRecord({
                          ...record,
                          [field.fieldName]: newValue,
                        });
                      }}
                      value={record[field.fieldName] ?? null}
                    />
                  )}
                  {field.uiType !== 'autocomplete' && (
                    <TextField
                      autoFocus={idx === 0}
                      disabled={
                        workingState?.adding ||
                        workingState?.updating ||
                        (field.fieldName === schema?.hierarchy?.primaryKey &&
                          mode === 'edit') ||
                        field.editable !== 'true'
                      }
                      required={field.required === 'Y'}
                      name={field.fieldName}
                      label={field.label}
                      multiline={field.uiType === 'textarea'}
                      rows={
                        field.uiType === 'textarea'
                          ? field.uiProperties.rows
                          : 1
                      }
                      value={record[field.fieldName] ?? ''}
                      onChange={handleInputChange}
                      fullWidth
                      className={classes.textField}
                    />
                  )}
                </div>
              </Tooltip>
            )}
            {field.type === 'bool' && (
              <Tooltip
                title={field.description}
                disableHoverListener
                placement="top-end"
              >
                <FormGroup className={classes.checkbox}>
                  <FormControlLabel
                    required={field.required === 'Y'}
                    label={field.label}
                    control={
                      <Checkbox
                        disabled={
                          workingState?.adding ||
                          workingState?.updating ||
                          (field.fieldName === schema?.hierarchy?.primaryKey &&
                            mode === 'edit') ||
                          field.editable !== 'true'
                        }
                        required={field.required === 'Y'}
                        name={field.fieldName}
                        checked={!!record[field.fieldName]}
                        onChange={(e) =>
                          setRecord({
                            ...record,
                            [e.target.name]: e.target.checked,
                          })
                        }
                      />
                    }
                  />
                </FormGroup>
              </Tooltip>
            )}
            {field.type === 'enum' && (
              <Tooltip
                title={field.description}
                disableHoverListener
                placement="top-end"
              >
                <Autocomplete
                  fullWidth
                  className={classes.autoComplete}
                  disabled={
                    workingState?.adding ||
                    workingState?.updating ||
                    (field.fieldName === schema?.hierarchy?.primaryKey &&
                      mode === 'edit') ||
                    field.editable !== 'true'
                  }
                  value={record[field.fieldName] ?? null}
                  onChange={(event, newValue) => {
                    setRecord({
                      ...record,
                      [field.fieldName]: newValue,
                    });
                  }}
                  options={schema.fields[field.fieldName]?.valueLabels || []}
                  sx={{ width: 300 }}
                  renderInput={(params: any) => (
                    <TextField {...params} label={field.label} />
                  )}
                />
              </Tooltip>
            )}
            {field.type === 'datetime' && (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Tooltip
                  title={field.description}
                  disableHoverListener
                  placement="top-end"
                >
                  <div>
                    <DateTimePicker
                      disabled={
                        workingState?.adding ||
                        workingState?.updating ||
                        (field.fieldName === schema?.hierarchy?.primaryKey &&
                          mode === 'edit') ||
                        field.editable !== 'true'
                      }
                      name={field.fieldName}
                      label={field.label}
                      value={record[field.fieldName] ?? null}
                      onChange={(nv) =>
                        setRecord({ ...record, [field.fieldName]: nv })
                      }
                      className={classes.dateTimeField}
                    />
                  </div>
                </Tooltip>
              </LocalizationProvider>
            )}
          </div>
        ))}
    </div>
  );
}
